/**
 * OvniLine Component
 *
 * Generates a dynamic SVG path for the cyan line and animates the UFO along it on scroll.
 * The line ends precisely at the ellipse edge of the gradient-curve-top element.
 *
 * ## Architecture
 * - The cyan line is drawn inside #ovniLineWrapper which has left: 8.3%, width: 87%
 * - The line must end at the ellipse edge of .gradient-curve-top::before
 * - The ellipse is created with CSS border-radius: 50% 50% 0 0 (semi-ellipse)
 *
 * ## Wave Pattern
 * The path flows like a fluid wave:
 * 1. Start at left, curve to max right (where next section begins)
 * 2. Curve back to max left (+200px down)
 * 3. Curve to slight right (+150px down)
 * 4. Continue down (+450px to manifesto text area)
 * 5. Go straight down (+120px to ellipse edge)
 *
 * ## Ellipse Calculation
 * The CSS ::before element has width: 110%, height: 100px, border-radius: 50% 50% 0 0
 * - radiusX = (110% * containerWidth) / 2
 * - radiusY = 100px / 2 = 50px
 * - yFromTop = radiusY - (radiusY * sqrt(1 - ((x - centerX) / radiusX)^2))
 */

/** Default UFO dimensions - actual size read from CSS */
const DEFAULT_UFO_WIDTH = 72;
const DEFAULT_UFO_HEIGHT = 170;

/**
 * Body left offset ratio within the UFO SVG.
 * The UFO body (dome) starts at x=13.21 in a 72px wide SVG.
 * This ratio is used to align the body with x=0 in mobile.
 */
const UFO_BODY_LEFT_RATIO = 13.21 / 72;

/**
 * Controls the cyan OVNI line SVG and UFO scroll animation.
 *
 * @example
 * ```typescript
 * import { initOvniLine } from './components/OvniLine';
 *
 * document.addEventListener('DOMContentLoaded', () => {
 *   const ovniLine = initOvniLine();
 *   // Later, to cleanup:
 *   // ovniLine?.destroy();
 * });
 * ```
 */
export class OvniLine {
  private wrapper: HTMLElement | null;
  private svg: SVGSVGElement | null;
  private path: SVGPathElement | null;
  private ovni: HTMLElement | null;
  private gradientCurve: HTMLElement | null;
  private manifestoSection: HTMLElement | null;

  private resizeTimeout: ReturnType<typeof setTimeout> | null = null;
  private isInitialized = false;

  /** Flag to enable/disable UFO movement along the path */
  private enableMovement = true;

  /** Flag to enable/disable debug visuals and logs */
  private enableDebug = false;

  /** Current UFO dimensions (read from CSS) */
  private ufoWidth = DEFAULT_UFO_WIDTH;
  private ufoHeight = DEFAULT_UFO_HEIGHT;

  constructor() {
    this.wrapper = document.getElementById('ovniLineWrapper');
    this.svg = document.getElementById('ovniPathSvg') as SVGSVGElement | null;
    this.path = document.getElementById('ovniPath') as SVGPathElement | null;
    this.ovni = document.getElementById('ovniShip');
    this.gradientCurve = document.querySelector('.gradient-curve-top');
    this.manifestoSection = document.querySelector('.manifesto-section');
  }

  /**
   * Initializes the component if all required DOM elements exist.
   * Generates the initial path and binds scroll/resize events.
   */
  public init(): void {
    if (!this.wrapper || !this.svg || !this.path || !this.ovni || !this.gradientCurve) {
      return;
    }

    this.isInitialized = true;
    this.updateUfoDimensions();
    this.generatePath();
    this.updateOvniPosition();
    this.bindEvents();

    // Show with fade after calculation
    requestAnimationFrame(() => {
      this.wrapper?.classList.add('is-ready');
    });
  }

  /**
   * Reads the current UFO dimensions from the CSS-styled element.
   * This allows responsive sizing through CSS media queries.
   */
  private updateUfoDimensions(): void {
    if (!this.ovni) return;

    const computed = window.getComputedStyle(this.ovni);
    this.ufoWidth = parseFloat(computed.width) || DEFAULT_UFO_WIDTH;
    this.ufoHeight = parseFloat(computed.height) || DEFAULT_UFO_HEIGHT;
  }

  private resizeObserver: ResizeObserver | null = null;

  /** Binds scroll and resize event listeners */
  private bindEvents(): void {
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('resize', this.handleResize, { passive: true });

    // Hide initially to prevent "jump" due to layout shift
    if (this.wrapper) {
      this.wrapper.style.opacity = '0';
      this.wrapper.style.transition = 'opacity 0.5s ease';
    }

    // Recalculate and reveal when fonts are loaded
    document.fonts.ready.then(() => {
      this.handleResize();
      // Reveal after a short delay to ensure layout has settled
      setTimeout(() => {
        if (this.wrapper) this.wrapper.style.opacity = '1';
      }, 100);
    });

    // Also on window load to be safe
    window.addEventListener('load', () => {
      this.handleResize();
      if (this.wrapper) this.wrapper.style.opacity = '1';
    });

    // Use ResizeObserver to watch for specific element size changes
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        this.handleResize();
      });

      const heroDesc = document.getElementById('heroDescription');
      const arrow = document.getElementById('heroScrollArrow');

      if (heroDesc) this.resizeObserver.observe(heroDesc);
      if (arrow) this.resizeObserver.observe(arrow);
    }
  }

  /** Handles scroll events to update UFO position */
  private handleScroll = (): void => {
    if (this.isInitialized) {
      this.updateOvniPosition();
    }
  };

  /** Handles resize events with 50ms debounce to regenerate path */
  private handleResize = (): void => {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    this.resizeTimeout = setTimeout(() => {
      this.updateUfoDimensions();
      this.generatePath();
      this.updateOvniPosition();
    }, 50);
  };

  /**
   * Generates the SVG path that ends at the ellipse edge.
   *
   * Calculates the precise Y position where the line should end by:
   * 1. Getting the wrapper's left offset relative to curveTop
   * 2. Calculating where the ellipse edge is at that X position
   * 3. Setting the SVG height to reach exactly that point
   */
  private generatePath(): void {
    if (!this.wrapper || !this.svg || !this.path || !this.gradientCurve) return;

    const wrapperRect = this.wrapper.getBoundingClientRect();
    const curveRect = this.gradientCurve.getBoundingClientRect();
    const wrapperWidth = this.wrapper.offsetWidth;

    // Calculate absolute positions (accounting for scroll)
    const wrapperAbsoluteTop = wrapperRect.top + window.scrollY;
    const curveAbsoluteTop = curveRect.top + window.scrollY;

    // Calculate manifesto section position relative to wrapper
    let manifestoY = 0;
    if (this.manifestoSection) {
      const manifestoRect = this.manifestoSection.getBoundingClientRect();
      const manifestoAbsoluteTop = manifestoRect.top + window.scrollY;
      manifestoY = manifestoAbsoluteTop - wrapperAbsoluteTop;
    }

    // Ellipse properties from CSS ::before
    // ::before has width=110%, height=100px, border-radius: 50% 50% 0 0
    const curveTopWidth = curveRect.width;
    const ellipseWidth = curveTopWidth * 1.1;
    const ellipseBeforeHeight = 100;
    const ellipseCenterX = curveTopWidth / 2;
    const ellipseRadiusX = ellipseWidth / 2;
    // CSS border-radius: 50% uses HALF the height as vertical radius
    const ellipseRadiusY = ellipseBeforeHeight / 2;

    // Line ends at X=0 in wrapper coords, translate to curveTop coords
    const endXInWrapper = 0;
    const wrapperLeftOffset = wrapperRect.left - curveRect.left;
    const endXInCurveTop = wrapperLeftOffset + endXInWrapper;

    // Calculate ellipse Y using the standard ellipse formula
    // CSS border-radius: 50% 50% 0 0 creates curve at TOP of element
    const xRelative = endXInCurveTop - ellipseCenterX;
    const normalized = Math.max(-1, Math.min(1, xRelative / ellipseRadiusX));
    const ellipseYFromBottom = ellipseRadiusY * Math.sqrt(1 - normalized * normalized);

    // Convert to Y from top (curve is at top of ::before)
    const ellipseYFromTop = ellipseRadiusY - ellipseYFromBottom;
    const ellipseEdgeY = ellipseYFromTop;

    // Calculate total SVG height to reach the ellipse edge
    const targetY = curveAbsoluteTop + ellipseEdgeY;
    const totalHeight = targetY - wrapperAbsoluteTop;

    // Set SVG dimensions
    this.svg.setAttribute('width', String(wrapperWidth));
    this.svg.setAttribute('height', String(totalHeight));
    this.svg.setAttribute('viewBox', `0 0 ${wrapperWidth} ${totalHeight}`);

    const pathD = this.buildPathD(wrapperWidth, totalHeight, manifestoY);
    this.path.setAttribute('d', pathD);
  }

  /**
   * Builds the SVG path string with smooth wave-like bezier curves.
   *
   * The path flows like a wave:
   * 1. Start at left, curve convex down to max right (where manifesto section begins)
   * 2. Curve back to max left (+200px down)
   * 3. Curve to slight right (+150px down)
   * 4. Continue down (+450px to manifesto text area)
   * 5. Go straight down (+120px to ellipse edge)
   *
   * @param width - Available width for the path
   * @param height - Total height to the ellipse edge
   * @param manifestoY - Y position where manifesto section starts (relative to wrapper)
   * @returns SVG path "d" attribute string
   */
  private buildPathD(width: number, height: number, manifestoY: number): string {
    // 1. Determine Start Position (Universal)
    // Desktop: offset startX by half UFO width so UFO doesn't go outside container
    // Mobile: offset so UFO body aligns with x=0 (body starts at UFO_BODY_LEFT_RATIO of width)
    const bodyCenterOffset = this.ufoWidth * (0.5 - UFO_BODY_LEFT_RATIO);
    const startX = window.innerWidth > 1024 ? this.ufoWidth / 2 : bodyCenterOffset;
    let startY = 0;
    let mobilePathPrefix = '';

    const heroDesc = document.getElementById('heroDescription');
    const arrow = document.getElementById('heroScrollArrow');
    const wrapper = document.getElementById('ovniLineWrapper');

    if (heroDesc && wrapper) {
      const wrapperRect = wrapper.getBoundingClientRect();
      const descRect = heroDesc.getBoundingClientRect();

      // Calculate relative Y positions
      const relativeDescBottom = descRect.bottom - wrapperRect.top;

      // Desktop: 60px below description bottom, Mobile: 16px above
      if (window.innerWidth > 1024) {
        startY = relativeDescBottom + 60;
      } else {
        startY = relativeDescBottom - 16;
      }

      if (this.enableDebug) {
        console.log('[OvniLine] StartY Debug:', {
          scrollY: window.scrollY,
          wrapperTop: wrapperRect.top,
          descBottom: descRect.bottom,
          relativeDescBottom,
          startY,
          wrapperAbsoluteTop: wrapperRect.top + window.scrollY,
          descAbsoluteBottom: descRect.bottom + window.scrollY,
        });
      }
    }

    // Mobile/Tablet specific: Turn point logic to avoid arrow
    if (window.innerWidth <= 1024 && arrow && wrapper) {
      const wrapperRect = wrapper.getBoundingClientRect();
      const arrowRect = arrow.getBoundingClientRect();
      const relativeArrowTop = arrowRect.top - wrapperRect.top;
      const relativeArrowBottom = arrowRect.bottom - wrapperRect.top;

      let turnY;

      if (window.innerWidth <= 481) {
        // Mobile: Curve starts BELOW the arrow
        turnY = relativeArrowBottom + 20;
      } else {
        // Tablet: Curve starts near top of arrow
        turnY = relativeArrowTop - 20;
      }

      // Use smooth curve instead of straight line (like reference SVG)
      // Reference uses: C startX startY startX midY startX turnY
      const midY = startY + (turnY - startY) * 0.5;
      mobilePathPrefix = `M ${startX} ${startY} C ${startX} ${startY}, ${startX} ${midY}, ${startX} ${turnY}`;

      // Curve starts from the turn point
      startY = turnY;
    }

    // 2. Calculate key Y positions
    let y1, y2, y3, straightStart;

    if (window.innerWidth <= 1024) {
      // Mobile/Tablet adjustments (extended to 1024px to keep Max Right above manifesto)
      // y1 is 106px before manifesto
      // y2 (maxLeft) is 4px after manifesto (+30px lower than before)
      // y3 (slightRight) is 90px after y2 (+40px lower than before)
      y1 = manifestoY > 0 ? manifestoY - 106 : height * 0.2;
      y2 = manifestoY > 0 ? manifestoY + 4 : y1 + 110;
      y3 = y2 + 90;
      straightStart = Math.min(y3 + 150, height - 50);
    } else {
      // Desktop (original logic)
      y1 = manifestoY > 0 ? manifestoY : height * 0.25;
      y2 = y1 + 200;
      y3 = y2 + 150;
      straightStart = Math.min(y3 + 250, height - 50);
    }

    // X positions using 6-column grid system
    // Grid gap: 5px (<768px), 20px (≥768px)
    const gap = window.innerWidth < 768 ? 5 : 20;
    const totalGaps = 5 * gap;
    const colWidth = (width - totalGaps) / 6;

    /**
     * Calculate X position for a given column number in 6-col grid
     * @param col - Column number (can be decimal, e.g., 2.2)
     */
    const getColPosition = (col: number): number => {
      const fullCols = Math.floor(col);
      const fraction = col - fullCols;
      return fullCols * colWidth + fullCols * gap + fraction * colWidth;
    };

    // maxRight: col 5.5 on mobile/tablet (1-indexed: col 6.5), near edge on desktop
    let maxRight = width * 0.999;
    if (window.innerWidth <= 1024) {
      maxRight = getColPosition(4.5); // 1-indexed col 5.5
    }

    // maxLeft: 1-indexed col 2.2 = 0-indexed col 1.2
    const maxLeft = getColPosition(1.2);

    // slightRight: 1-indexed col 2.8 = 0-indexed col 1.8
    const slightRight = getColPosition(1.8);

    if (this.enableDebug) {
      console.log('[OvniLine] Column-based X positions:', {
        gap,
        colWidth,
        maxLeft: `col 2.2 = ${maxLeft}px`,
        slightRight: `col 2.8 = ${slightRight}px`,
        maxRight: `col 5.5 = ${maxRight}px`,
      });
    }

    // 3. Calculate Curve Control Points
    let intermediateX, intermediateY;
    let c0_cp1x, c0_cp1y, c0_cp2x, c0_cp2y, c0_endX, c0_endY;
    let c1_cp1x, c1_cp1y, c1_cp2x, c1_cp2y, c1_endX, c1_endY;
    let c2_cp1x, c2_cp1y, c2_cp2x, c2_cp2y, c2_endX, c2_endY;
    let c3_cp1x, c3_cp1y, c3_cp2x, c3_cp2y, c3_endX, c3_endY;
    let c4_cp1x, c4_cp1y, c4_cp2x, c4_cp2y, c4_endX, c4_endY;

    if (window.innerWidth <= 1024) {
      // MOBILE/TABLET GEOMETRY - Structural Anchor (extended to 1024px)

      // Default values
      intermediateX = width * 0.35;

      // Calculate safe Y and X to clear the arrow
      let safeY = startY + 100;

      if (arrow && wrapper) {
        const wrapperRect = wrapper.getBoundingClientRect();
        const arrowRect = arrow.getBoundingClientRect();

        // Structural Anchor: Center of arrow
        // "el segundo punto no esta por debajo del arrow en una posicion x" -> Now it will be.
        intermediateX = arrowRect.left + arrowRect.width / 2 - wrapperRect.left;

        // Ensure we are below the arrow bottom
        safeY = arrowRect.bottom - wrapperRect.top + 40;

        if (this.enableDebug) {
          console.log('[OvniLine] Tablet Structural Anchor:', {
            arrowCenter: intermediateX,
            arrowBottom: arrowRect.bottom,
            safeY,
          });
        }
      }

      // Interpolate intermediateY but enforce safety
      // We want it to be at least safeY to ensure the curve passes UNDER the arrow level
      const calculatedIntermediate = startY + (y1 - startY) * 0.55;
      intermediateY = Math.max(calculatedIntermediate, safeY);

      // Curve 0: Start -> Intermediate
      // CP1: goes straight DOWN from start (vertical handle) - creates outward bulge
      c0_cp1x = startX;
      c0_cp1y = startY + (intermediateY - startY) * 0.7;

      // Calculate handle direction for smooth continuity at intermediate point
      // Handles at intermediate must be colinear (180°)
      // Base angle rotated -40° from horizontal
      const handleLen = Math.min(width * 0.15, 60);
      const angleRad = (10 * Math.PI) / 180; // -40 degrees in radians
      const handleDirX = Math.cos(angleRad);
      const handleDirY = Math.sin(angleRad);

      // CP2: handle coming INTO intermediate - points in negative direction
      c0_cp2x = intermediateX - handleLen * handleDirX;
      c0_cp2y = intermediateY - handleLen * handleDirY;

      c0_endX = intermediateX;
      c0_endY = intermediateY;

      // Curve 1: Intermediate -> Max Right
      // CP1: handle going OUT of intermediate - opposite direction (180°), colinear with CP2
      c1_cp1x = intermediateX + handleLen * handleDirX;
      c1_cp1y = intermediateY + handleLen * handleDirY;

      // Use FIXED max values to prevent distortion at larger sizes
      const handleOffsetX = Math.min(width * 0.15, 50); // Max 50px
      const handleOffsetY = Math.min(width * 0.07, 30); // Max 30px

      // Anchor at maxRight
      c1_endX = maxRight;
      c1_endY = y1;

      // CP2: comes from RIGHT of anchor (horizontal-ish) for smooth entry
      c1_cp2x = maxRight + handleOffsetX;
      c1_cp2y = y1 - handleOffsetY;
    } else {
      // DESKTOP GEOMETRY - Structural/Relative Approach

      // Default values if elements are missing
      intermediateX = 100;
      intermediateY = height * 0.4;

      if (arrow && wrapper) {
        const wrapperRect = wrapper.getBoundingClientRect();
        const arrowRect = arrow.getBoundingClientRect();

        // Position second point at midpoint between startX (0) and arrow left edge, plus offset
        const arrowLeftRelative = arrowRect.left - wrapperRect.left;
        intermediateX = arrowLeftRelative / 2 + 100;

        const arrowRelBottom = arrowRect.bottom - wrapperRect.top;

        // Context-Aware Clearance
        // Reduced clearance as requested ("podrían estar mas arriba")
        const minClearance = 30; // Was 40
        const targetClearance = 60; // Was 80
        const maxDrop = y1 - 20;

        const minY = arrowRelBottom + minClearance;
        const targetY = arrowRelBottom + targetClearance;

        intermediateY = Math.max(minY, Math.min(targetY, maxDrop));

        if (this.enableDebug) {
          console.log('[OvniLine] Desktop Structural Anchor:', {
            arrowCenter: intermediateX,
            arrowRelBottom,
            y1,
            calculatedIntermediateY: intermediateY,
          });
        }
      } else {
        intermediateX = width * 0.5;
        intermediateY = Math.max(startY + 100, y1 * 0.5);
      }

      // Curve 0: Start -> Intermediate (Under Arrow)
      // CP1: goes straight DOWN from start (vertical handle) - creates outward bulge
      // Reduced factor for smoother initial curve on large screens
      c0_cp1x = startX;
      c0_cp1y = startY + (intermediateY - startY) * 0.55;

      // Calculate handle direction for smooth continuity at intermediate point
      // Handles at intermediate must be colinear (180°)
      // Base angle rotated +10° from horizontal
      // Increased limits for smoother curves on large screens
      const handleLenDesktop = Math.min(width * 0.12, 180);
      const angleRadDesktop = (15 * Math.PI) / 180; // +15 degrees for smoother curve
      const handleDirXDesktop = Math.cos(angleRadDesktop);
      const handleDirYDesktop = Math.sin(angleRadDesktop);

      // CP2: handle coming INTO intermediate - points in negative direction
      c0_cp2x = intermediateX - handleLenDesktop * handleDirXDesktop;
      c0_cp2y = intermediateY - handleLenDesktop * handleDirYDesktop;

      c0_endX = intermediateX;
      c0_endY = intermediateY;

      // Curve 1: Intermediate (Under Arrow) -> Max Right
      // CP1: handle going OUT of intermediate - opposite direction (180°), colinear with CP2
      c1_cp1x = intermediateX + handleLenDesktop * handleDirXDesktop;
      c1_cp1y = intermediateY + handleLenDesktop * handleDirYDesktop;

      // Anchor point inward from max
      const anchorInset = 60;

      c1_endX = maxRight - anchorInset;
      c1_endY = y1;

      // Handle extends far right AND is ABOVE the anchor point
      c1_cp2x = maxRight + 200;
      c1_cp2y = y1 - 80; // Above the anchor
    }

    // Common Curves 2-4 (Desktop & Mobile shared logic structure, though points differ)
    // Actually, I should keep the desktop/mobile split for 2-4 as they were different in previous code.
    // My previous code had them inside the else block for desktop.
    // I need to restore them.

    if (window.innerWidth <= 1024) {
      // Mobile/Tablet Curves 2-3 (extended to 1024px)
      // Use FIXED max values to prevent distortion at larger sizes

      // Curve 2: Max Right -> Max Left
      // CP1: LEFT of anchor AND BELOW it
      const c2HandleOffsetX = Math.min(width * 0.1, 40);
      const c2HandleOffsetY = Math.min(width * 0.07, 25);
      c2_cp1x = c1_endX - c2HandleOffsetX;
      c2_cp1y = c1_endY + c2HandleOffsetY;
      // CP2: LEFT of maxLeft AND ABOVE it
      c2_cp2x = maxLeft - Math.min(width * 0.12, 50);
      c2_cp2y = y2 - Math.min(width * 0.18, 60);
      c2_endX = maxLeft;
      c2_endY = y2;

      // Curve 3: Max Left -> Straight
      // CP1: RIGHT of maxLeft AND BELOW it
      // End X should be same as startX to align with UFO start position
      const straightX = startX;
      c3_cp1x = maxLeft + Math.min(width * 0.25, 80);
      c3_cp1y = y2 + Math.min(width * 0.35, 100);
      // CP2: at straightX, ABOVE straightStart
      c3_cp2x = straightX;
      c3_cp2y = straightStart - Math.min(width * 0.5, 150);
      c3_endX = straightX;
      c3_endY = straightStart;

      // Curve 4: Not needed - set to same as curve 3 end (will be skipped in path)
      c4_cp1x = straightX;
      c4_cp1y = straightStart;
      c4_cp2x = straightX;
      c4_cp2y = straightStart;
      c4_endX = straightX;
      c4_endY = straightStart;
    } else {
      // Desktop Curves 2-3
      // Following line-ovni.html pattern: maxRight -> maxLeft -> straight down

      // Curve 2: Max Right -> Max Left
      // CP1: LEFT of maxRight anchor AND BELOW it
      c2_cp1x = c1_endX - 150;
      c2_cp1y = c1_endY + 60;
      // CP2: LEFT of maxLeft AND ABOVE it
      c2_cp2x = maxLeft - 180;
      c2_cp2y = y2 - 100;
      c2_endX = maxLeft;
      c2_endY = y2;

      // Curve 3: Max Left -> Straight (like line-ovni.html: single curve, no slightRight)
      // CP1: RIGHT of maxLeft AND BELOW it (like original: 727 > 325)
      // End X should be same as startX to align with UFO start position
      const straightX = startX;
      c3_cp1x = maxLeft + 250;
      c3_cp1y = y2 + 180;
      // CP2: at straightX, ABOVE straightStart
      c3_cp2x = straightX;
      c3_cp2y = straightStart - 250;
      c3_endX = straightX;
      c3_endY = straightStart;

      // Curve 4: Not needed - set to same as curve 3 end
      c4_cp1x = straightX;
      c4_cp1y = straightStart;
      c4_cp2x = straightX;
      c4_cp2y = straightStart;
      c4_endX = straightX;
      c4_endY = straightStart;
    }

    // Straight line down to ellipse edge (use same X as curve end)
    const endX = c3_endX;
    const endY = height;

    // DEBUG: Draw control points with handles
    if (this.enableDebug) {
      this.drawDebugPoints([
        // Curve 0
        {
          start: { x: startX, y: startY },
          cp1: { x: c0_cp1x, y: c0_cp1y },
          cp2: { x: c0_cp2x, y: c0_cp2y },
          end: { x: c0_endX, y: c0_endY },
          color: 'red',
        },
        // Curve 1
        {
          start: { x: c0_endX, y: c0_endY },
          cp1: { x: c1_cp1x, y: c1_cp1y },
          cp2: { x: c1_cp2x, y: c1_cp2y },
          end: { x: c1_endX, y: c1_endY },
          color: 'blue',
        },
        // Curve 2
        {
          start: { x: c1_endX, y: c1_endY },
          cp1: { x: c2_cp1x, y: c2_cp1y },
          cp2: { x: c2_cp2x, y: c2_cp2y },
          end: { x: c2_endX, y: c2_endY },
          color: 'green',
        },
      ]);

      console.log('[OvniLine] Curve 0 Config:', {
        start: { x: startX, y: startY },
        cp1: { x: c0_cp1x, y: c0_cp1y },
        cp2: { x: c0_cp2x, y: c0_cp2y },
        end: { x: c0_endX, y: c0_endY },
      });

      console.log('[OvniLine] Curve 1 Config:', {
        start: { x: c0_endX, y: c0_endY },
        cp1: { x: c1_cp1x, y: c1_cp1y },
        cp2: { x: c1_cp2x, y: c1_cp2y },
        end: { x: c1_endX, y: c1_endY },
      });
    }

    // Build path - skip curve 4 if it's degenerate (all points same)
    const curves = [
      mobilePathPrefix || `M ${startX} ${startY}`,
      `C ${c0_cp1x} ${c0_cp1y}, ${c0_cp2x} ${c0_cp2y}, ${c0_endX} ${c0_endY}`,
      `C ${c1_cp1x} ${c1_cp1y}, ${c1_cp2x} ${c1_cp2y}, ${c1_endX} ${c1_endY}`,
      `C ${c2_cp1x} ${c2_cp1y}, ${c2_cp2x} ${c2_cp2y}, ${c2_endX} ${c2_endY}`,
      `C ${c3_cp1x} ${c3_cp1y}, ${c3_cp2x} ${c3_cp2y}, ${c3_endX} ${c3_endY}`,
    ];

    // Only add curve 4 if it's not degenerate
    if (c4_endX !== c3_endX || c4_endY !== c3_endY) {
      curves.push(`C ${c4_cp1x} ${c4_cp1y}, ${c4_cp2x} ${c4_cp2y}, ${c4_endX} ${c4_endY}`);
    }

    curves.push(`L ${endX} ${endY}`);
    return curves.join(' ');
  }

  /**
   * Draws debug visuals: circles for points and lines for handles.
   */
  private drawDebugPoints(
    curves: {
      start: { x: number; y: number };
      cp1: { x: number; y: number };
      cp2: { x: number; y: number };
      end: { x: number; y: number };
      color: string;
    }[]
  ): void {
    // Remove existing debug group
    const existingGroup = document.getElementById('ovniDebugGroup');
    if (existingGroup) existingGroup.remove();

    const group = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    group.id = 'ovniDebugGroup';

    curves.forEach((curve) => {
      // Draw Handle 1: Start -> CP1
      const line1 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      line1.setAttribute('x1', String(curve.start.x));
      line1.setAttribute('y1', String(curve.start.y));
      line1.setAttribute('x2', String(curve.cp1.x));
      line1.setAttribute('y2', String(curve.cp1.y));
      line1.setAttribute('stroke', curve.color);
      line1.setAttribute('stroke-width', '1');
      line1.setAttribute('stroke-dasharray', '4');
      group.appendChild(line1);

      // Draw Handle 2: End -> CP2
      const line2 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      line2.setAttribute('x1', String(curve.end.x));
      line2.setAttribute('y1', String(curve.end.y));
      line2.setAttribute('x2', String(curve.cp2.x));
      line2.setAttribute('y2', String(curve.cp2.y));
      line2.setAttribute('stroke', curve.color);
      line2.setAttribute('stroke-width', '1');
      line2.setAttribute('stroke-dasharray', '4');
      group.appendChild(line2);

      // Draw Points
      const points = [
        { ...curve.start, r: 3, fill: curve.color }, // Start
        { ...curve.cp1, r: 2, fill: curve.color }, // CP1
        { ...curve.cp2, r: 2, fill: curve.color }, // CP2
        { ...curve.end, r: 3, fill: curve.color }, // End
      ];

      points.forEach((p) => {
        const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        circle.setAttribute('cx', String(p.x));
        circle.setAttribute('cy', String(p.y));
        circle.setAttribute('r', String(p.r));
        circle.setAttribute('fill', p.fill);
        group.appendChild(circle);
      });
    });

    this.svg?.appendChild(group);
  }

  /**
   * Updates the UFO position along the path.
   *
   * The UFO starts at the beginning of the path (scroll = 0) and moves
   * faster initially to catch up with the viewport center, then settles
   * into normal scroll-following behavior.
   *
   * Also makes the already-traveled portion of the line transparent using stroke-dasharray.
   */
  private updateOvniPosition(): void {
    if (!this.path || !this.ovni || !this.wrapper) return;

    const pathLength = this.path.getTotalLength();
    if (pathLength === 0) return;

    // Check if movement is enabled
    if (!this.enableMovement) {
      // If disabled, keep UFO at the very start of the path
      const startPoint = this.path.getPointAtLength(0);
      const offsetX = startPoint.x - this.ufoWidth / 2;
      const offsetY = startPoint.y - this.ufoHeight;
      this.ovni.style.transform = `translate(${offsetX}px, ${offsetY}px)`;
      // Show full line
      this.path.style.strokeDasharray = 'none';
      this.path.style.strokeDashoffset = '0';
      return;
    }

    const wrapperRect = this.wrapper.getBoundingClientRect();
    const startPoint = this.path.getPointAtLength(0);

    // Calculate where the path starts relative to viewport
    const pathStartInViewport = wrapperRect.top + startPoint.y;

    // Target Y position: center of viewport
    const viewportCenterY = window.innerHeight / 2;

    // Calculate the "natural" target on the path (where viewport center intersects)
    const naturalTargetY = viewportCenterY - wrapperRect.top + this.ufoHeight / 2;

    // When scroll is at top, pathStartInViewport is positive (path is below top of screen)
    // We want UFO to start ON the line, then accelerate to catch up to viewport center

    // Calculate how far into the page we've scrolled relative to path start
    const scrollProgress = Math.max(0, viewportCenterY - pathStartInViewport);

    // The distance the UFO needs to "catch up" is from path start to where it would
    // naturally be if following viewport center
    const catchUpDistance = naturalTargetY - startPoint.y;

    let targetPathY: number;
    let currentPathLength: number;

    if (catchUpDistance <= 0 || scrollProgress <= 0) {
      // UFO is at or before the start - keep it at start
      targetPathY = startPoint.y;
      currentPathLength = 0;
    } else {
      // Apply easing: UFO moves faster at the start to catch up
      // Use a curve that starts fast and slows down
      const maxCatchUp = 300; // pixels of scroll before UFO catches up to normal
      const catchUpProgress = Math.min(1, scrollProgress / maxCatchUp);

      // Ease-out curve: fast at start, slows down
      const eased = 1 - Math.pow(1 - catchUpProgress, 2);

      // Interpolate between starting at path start and following viewport center
      targetPathY = startPoint.y + (naturalTargetY - startPoint.y) * eased;
    }

    // Find the point on the path at this Y value and get the path length at that point
    const result = this.findPointAndLengthAtY(targetPathY, pathLength);
    const point = result.point;
    currentPathLength = result.length;

    // UFO is positioned so its bottom touches the line (no gap)
    const offsetX = point.x - this.ufoWidth / 2;
    const offsetY = point.y - this.ufoHeight;

    this.ovni.style.transform = `translate(${offsetX}px, ${offsetY}px)`;

    // Make the traveled portion transparent using stroke-dasharray
    // Pattern: 0 gap (invisible), then currentPathLength gap (invisible), then rest visible
    // This works cross-browser (Safari interprets negative offset differently)
    const remainingLength = pathLength - currentPathLength;
    this.path.style.strokeDasharray = `0 ${currentPathLength} ${remainingLength}`;
    this.path.style.strokeDashoffset = '0';
  }

  /**
   * Finds the point on the path closest to the target Y value and returns both
   * the point and the path length at that position.
   * Uses binary search for efficiency.
   */
  private findPointAndLengthAtY(
    targetY: number,
    pathLength: number
  ): { point: DOMPoint; length: number } {
    if (!this.path) return { point: new DOMPoint(0, 0), length: 0 };

    // Clamp targetY to valid range
    const startPoint = this.path.getPointAtLength(0);

    // If target is before start, return start
    if (targetY <= startPoint.y) {
      return { point: startPoint, length: 0 };
    }

    // If target is after end, stop 10px before the end
    const stopBeforeEnd = 10;
    const maxLength = pathLength - stopBeforeEnd;
    const maxPoint = this.path.getPointAtLength(maxLength);
    if (targetY >= maxPoint.y) {
      return { point: maxPoint, length: maxLength };
    }

    // Binary search to find the length where Y = targetY
    let low = 0;
    let high = pathLength;
    let mid: number;
    let point: DOMPoint;

    // 20 iterations gives us precision of pathLength / 2^20
    for (let i = 0; i < 20; i++) {
      mid = (low + high) / 2;
      point = this.path.getPointAtLength(mid);

      if (point.y < targetY) {
        low = mid;
      } else {
        high = mid;
      }
    }

    const finalLength = (low + high) / 2;
    return {
      point: this.path.getPointAtLength(finalLength),
      length: finalLength,
    };
  }

  /**
   * Cleans up event listeners and resets state.
   * Call this when removing the component from the page.
   */
  public destroy(): void {
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);

    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    this.isInitialized = false;
  }
}

/**
 * Factory function to create and initialize an OvniLine instance.
 *
 * @returns The initialized OvniLine instance, or null if initialization failed
 */
export function initOvniLine(): OvniLine | null {
  const ovniLine = new OvniLine();
  ovniLine.init();
  return ovniLine;
}
